import React, { useEffect, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import FileTree from '../common/FileTree';
import useStageJob from '../common/useStageJob';
import AgentActivityGraph from '../common/AgentActivityGraph';
import {
  getProject,
  setStageData,
  patchStage,
  subscribe,
  buildStageContext,
} from '../../../services/pipelineProject.service';
import { runPipelineStage } from '../../../services/pipeline.service';
import '../Pipeline.css';

/**
 * Code Stage
 *
 * Generates a complete, deployment-ready project with full folder structure,
 * source files, Dockerfile, env vars, and setup instructions.
 */
function CodeStage() {
  const [project, setProject] = useState(getProject());
  const [codeData, setCodeData] = useState(null);
  const [error, setError]       = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    const p = getProject();
    if (p.stages?.code?.data) setCodeData(p.stages.code.data);
    return subscribe(setProject);
  }, []);

  const handleResult = (result) => {
    setCodeData(result || {});
    setStageData('code', result || {});
    setError(null);
  };

  const handleActivity = (acts) => setActivity(acts || []);
  const { polling, error: pollError, startPolling } = useStageJob(handleResult, setError, handleActivity);

  const handleGenerate = async () => {
    setError(null);
    const context = buildStageContext('code');
    const comment = project?.stages?.code?.comment || '';
    try {
      const res = await runPipelineStage('code', context, comment);
      if (res.status === 'accepted') {
        patchStage('code', { jobId: res.jobId });
        startPolling(res.jobId);
      } else if (res.status === 'completed') {
        handleResult(res.result);
        if (res.agentActivity) setActivity(res.agentActivity);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate code.');
    }
  };

  const docs       = project?.stages?.docs?.data;
  const hasCode    = Boolean(codeData?.files?.length);
  const displayError = error || pollError;

  return (
    <PipelineShell
      stageId="code"
      description="Generate production-ready code with a complete folder structure, ready for direct deployment."
      canApprove={hasCode}
      headerAction={
        <button
          className="pipeline-btn primary"
          onClick={handleGenerate}
          disabled={polling || !docs}
          title={!docs ? 'Complete the Docs stage first' : undefined}
        >
          {polling ? '⏳ Generating…' : hasCode ? '↩ Regenerate' : '💻 Generate Code'}
        </button>
      }
    >
      {!docs && (
        <div className="pipeline-empty">
          <div className="pipeline-empty-icon">🔒</div>
          <p>Complete and approve the <strong>Docs</strong> stage first.</p>
        </div>
      )}

      {(polling || activity.length > 0) && (
        <AgentActivityGraph activity={activity} polling={polling} />
      )}

      {polling && (
        <div className="pipeline-loading">
          <div className="pipeline-spinner" />
          <p>Generating project code…</p>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>This may take up to 2 minutes.</p>
        </div>
      )}

      {displayError && !polling && <div className="pipeline-error">{displayError}</div>}

      {hasCode && !polling && (
        <>
          {/* Summary bar */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <span className="pipeline-file-count-badge">{codeData.files.length} files generated</span>
            {codeData.setupInstructions && (
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                📦 {codeData.setupInstructions.slice(0, 80)}…
              </span>
            )}
          </div>

          {/* File tree + Monaco viewer */}
          <FileTree files={codeData.files} />

          {/* Env vars */}
          {Array.isArray(codeData.envVars) && codeData.envVars.length > 0 && (
            <div className="pipeline-card" style={{ marginTop: 16 }}>
              <div className="pipeline-card-title">Environment Variables</div>
              <table className="pipeline-tech-table">
                <thead>
                  <tr><th>Name</th><th>Description</th><th>Required</th></tr>
                </thead>
                <tbody>
                  {codeData.envVars.map((v, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{v.name}</td>
                      <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{v.description}</td>
                      <td>
                        <span className={`pipeline-tag ${v.required ? 'red' : 'gray'}`}>
                          {v.required ? 'required' : 'optional'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </PipelineShell>
  );
}

export default CodeStage;
